import { assertWritesAllowed } from './write-gate';
import { graphStore, type GraphStore } from './graph-store';
import { EditableWrapper } from './editable-wrapper';
import { localUuid } from './uuid';
import {
  registerPendingLocalUuid,
  removePendingLocalUuid,
  checkAndApplyBufferedPatchesForAllContexts
} from './local-uuid-registry';

/**
 * NeoNodeBase - Generic base class for all Neo4j model instances
 *
 * Provides common functionality for CRUD operations:
 * - Constructor with data assignment
 * - API client management
 * - Generic save() method (POST /api/create or PATCH /api/modify)
 * - Generic delete() method (DELETE /api/delete)
 *
 * All generated model classes extend this to avoid code duplication.
 *
 * @example
 * ```typescript
 * export class Thread extends NeoNodeBase<ThreadReadAPI, ThreadWriteAPI> {
 *   protected get modelName() { return 'Thread'; }
 *   // ... model-specific properties and methods
 * }
 * ```
 */
/**
 * Global WeakMap to cache RelationshipManager instances per node instance.
 *
 * Using WeakMap provides automatic garbage collection:
 * - Key: The source node instance
 * - Value: Map of property names to RelationshipManager instances
 * - When the source instance is GC'd, the entire cache entry is automatically removed
 *
 * This prevents memory leaks even if instances are stored in long-lived global state.
 */
const relationshipManagerCache = new WeakMap<NeoNodeBase<any, any>, Map<string, any>>();

export abstract class NeoNodeBase<TRead, TWrite> {
  /**
   * Static API client shared across all model instances.
   * Set via setApiClient() before using any model operations.
   *
   * Note: The 'post' method actually makes PUT requests to /api/create
   * (Backend uses PUT for create to distinguish from POST for queries)
   */
  private static _apiClient?: {
    put: (path: string, data: any) => Promise<any>;  // Actually uses PUT for /create
    patch: (path: string, data: any) => Promise<any>;
    delete: (path: string, data: any) => Promise<any>;
  };

  /**
   * Model name for API requests (e.g., 'Thread', 'Step')
   * Must be implemented by subclass.
   */
  protected abstract get modelName(): string;

  /**
   * Node ID (UUID)
   */
  id?: string;

  /**
   * Monotonically increasing version number
   */
  version?: number;

  /**
   * Error from most recent failed save operation
   * Cleared on successful save
   */
  _saveError?: Error;


  /**
   * Constructor - assigns all provided data to instance and wraps in Proxy
   * @param data - Partial model data (WriteAPI format)
   */
  // Relationship data passed in constructor (for create API)
  _constructorRelationships?: Record<string, any>;

  constructor(data: Partial<TWrite> = {}) {
    // Filter out getter-only properties (they cannot be set via Object.assign)
    // These include _label and any relationship properties that have getters
    const cleanData: any = {};
    const relationshipData: any = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const descriptor = Object.getOwnPropertyDescriptor(this.constructor.prototype, key);
        // If property has a getter but no setter, it's a relationship - store separately
        if (descriptor && descriptor.get && !descriptor.set) {
          relationshipData[key] = (data as any)[key];
          continue;
        }
        cleanData[key] = (data as any)[key];
      }
    }

    Object.assign(this, cleanData);

    // Store relationship data for serialization in create API
    if (Object.keys(relationshipData).length > 0) {
      this._constructorRelationships = relationshipData;
    }

    // Register with GraphStore if we have an ID
    if (this.id) {
      this.registerInGraphStore();
    }
  }

  /**
   * Register this node in the GraphStore
   * Called automatically when node has an ID
   */
  protected registerInGraphStore(): void {
    if (this.id) {
      graphStore.registerNode(this);
    }
  }

  /**
   * Get the configured API client.
   * Throws if not configured - call setApiClient() first.
   */
  static get apiClient() {
    if (!NeoNodeBase._apiClient) {
      throw new Error('API client not configured. Call setApiClient() on your model class or NeoNodeBase before using.');
    }
    return NeoNodeBase._apiClient;
  }

  /**
   * Configure the API client for all model instances.
   * Should be called once during app initialization (e.g., in useNeoClient hook).
   *
   * @param client - API client with post, patch, delete methods
   */
  static setApiClient(client: {
    put: (path: string, data: any) => Promise<any>;
    patch: (path: string, data: any) => Promise<any>;
    delete: (path: string, data: any) => Promise<any>;
  }) {
    NeoNodeBase._apiClient = client;
  }

  /**
   * Recursively serialize a node instance for PATCH operations.
   * Collects both scalar properties and nested relationship operations.
   *
   * @param instance - Node instance to serialize
   * @param depth - Current nesting depth (for debugging/limits)
   * @returns Serialized object with scalars and relationship operations
   *
   * @example
   * Input: Step instance with model_requests.add([new ModelRequest({...})])
   * Output: {
   *   text: "Step text",
   *   model_requests: {
   *     add: [{ agent_name: "agent", user_prompt: "prompt" }]
   *   }
   * }
   */
  private static serializeForPatch(instance: any, depth: number = 0): any {
    if (!instance || typeof instance !== 'object') {
      return instance;
    }

    // Handle arrays
    if (Array.isArray(instance)) {
      return instance.map(item => NeoNodeBase.serializeForPatch(item, depth));
    }

    const result: any = {};

    // Serialize scalar properties
    for (const key in instance) {
      if (!instance.hasOwnProperty(key)) continue;

      // Skip internal/private fields
      if (key.startsWith('_')) continue;

      // Skip methods
      if (typeof instance[key] === 'function') continue;

      const value = instance[key];

      // Check if this is a RelationshipManager (has hasPendingOps method)
      if (value && typeof value === 'object' && typeof value.hasPendingOps !== 'undefined') {
        // This is a relationship field - skip for now, we'll process it separately
        continue;
      }

      // Regular scalar property - include it
      result[key] = value;
    }

    // Get relationship properties if this is a NeoNodeBase instance
    if (instance.getRelationshipProperties && typeof instance.getRelationshipProperties === 'function') {
      const relationshipProps = instance.getRelationshipProperties();

      for (const propName of relationshipProps) {
        const manager = instance[propName];
        if (!manager?.hasPendingOps) continue;

        const pendingOps = manager.getPendingOps();
        const serializedOps: any = {};

        // Recursively serialize items in 'add' operation
        if (pendingOps.add) {
          const items = Array.isArray(pendingOps.add) ? pendingOps.add : [pendingOps.add];
          serializedOps.add = items.map((item: any) => {
            // If item has an ID only, it's a reference to existing object
            if (item && typeof item === 'object' && item.id && Object.keys(item).length === 1) {
              return { id: item.id };
            }
            // Otherwise, recursively serialize (new object with potential nested ops)
            return NeoNodeBase.serializeForPatch(item, depth + 1);
          });
        }

        // Serialize 'remove' operation (should only be {id: ...} refs)
        if (pendingOps.remove) {
          const items = Array.isArray(pendingOps.remove) ? pendingOps.remove : [pendingOps.remove];
          serializedOps.remove = items.map((item: any) => {
            if (typeof item === 'string') return { id: item };
            return { id: item.id };
          });
        }

        // Serialize 'set' operation
        if (pendingOps.set !== undefined) {
          if (pendingOps.set === null) {
            serializedOps.set = null;
          } else {
            const items = Array.isArray(pendingOps.set) ? pendingOps.set : [pendingOps.set];
            serializedOps.set = items.map((item: any) => {
              if (item && typeof item === 'object' && item.id && Object.keys(item).length === 1) {
                return { id: item.id };
              }
              return NeoNodeBase.serializeForPatch(item, depth + 1);
            });
          }
        }

        // Serialize 'modify' operation (for existing objects with nested ops)
        if (pendingOps.modify) {
          const items = Array.isArray(pendingOps.modify) ? pendingOps.modify : [pendingOps.modify];
          serializedOps.modify = items.map((item: any) => {
            if (!item.id) {
              throw new Error(`'modify' operation requires objects with 'id' field`);
            }
            // Recursively serialize to get nested relationship ops
            const serialized = NeoNodeBase.serializeForPatch(item, depth + 1);
            // Ensure id is preserved
            serialized.id = item.id;
            return serialized;
          });
        }

        if (Object.keys(serializedOps).length > 0) {
          result[propName] = serializedOps;
        }
      }
    }

    return result;
  }

  /**
   * Save this instance to the server.
   *
   * - If no ID: PUT /api/create (create new node)
   * - If has ID: Use makeEditable() pattern for modifications
   *
   * @returns This instance with updated data from server
   *
   * @example
   * ```typescript
   * // CREATE - save() works for new instances
   * const thread = new Thread({ name: 'My Thread' });
   * const saved = await thread.save();  // PUT /api/create
   *
   * // MODIFY - use makeEditable() pattern
   * const editable = thread.makeEditable();
   * editable.name = 'Updated';
   * editable.steps.add(newStep);
   * await editable.save();  // PATCH /api/modify
   * ```
   */
  async save(): Promise<this> {
    // writes paused (a page the server has not confirmed): nothing is sent,
    // nothing is changed here
    assertWritesAllowed();
    const client = (this.constructor as typeof NeoNodeBase).apiClient;

    // Check if this is a new instance:
    // - No ID, OR
    // - Has a local UUID (local-* prefix) which indicates client-generated ID for new object
    const isNewInstance = !this.id || this.id.startsWith('local-');

    if (isNewInstance) {
      // CREATE - New instance (no ID or local UUID)
      // Uses client.put() which actually makes a PUT request to /create

      // Generate local UUID for this instance if it doesn't have one
      // This enables optimistic UI and DAG patch buffering
      const pendingLocalUuids: string[] = [];
      if (!this.id) {
        const localId = localUuid();
        (this as any).id = localId;
        (this as any)._localUuid = localId;
        pendingLocalUuids.push(localId);
      } else if (this.id.startsWith('local-')) {
        pendingLocalUuids.push(this.id);
        (this as any)._localUuid = this.id;
      }

      // Collect local UUIDs from nested items
      this.collectNestedLocalUuids(pendingLocalUuids);

      // Register all local UUIDs as pending BEFORE sending request
      // This ensures DAG patches with these UUIDs are buffered
      for (const uuid of pendingLocalUuids) {
        registerPendingLocalUuid(uuid);
      }

      try {
        // Build create data, merging constructor relationships at top level
        const createData: any = { ...this };
        if (this._constructorRelationships) {
          Object.assign(createData, this._constructorRelationships);
          delete createData._constructorRelationships;
        }
        delete createData._saveError;

        const response = await client.put('/api/create', {
          model_name: this.modelName,
          data: createData
        });

        // Handle new response format with uuid_map
        const nodeData = response.uuid_map ? response.data : response;
        const uuidMap: Record<string, string> = response.uuid_map || {};

        // Update this instance with server response data
        Object.assign(this, nodeData);

        // Remove pending local UUIDs now that we have the mapping
        for (const localId of Object.keys(uuidMap)) {
          removePendingLocalUuid(localId);
        }

        // Apply any buffered DAG patches now that UUIDs are resolved
        checkAndApplyBufferedPatchesForAllContexts();

        // Register in GraphStore
        if (this.id) {
          graphStore.registerNode(this);
        }

        // Clear error on successful save
        this._saveError = undefined;

        return this;
      } catch (error) {
        // On error, remove pending local UUIDs to unblock DAG patches
        for (const uuid of pendingLocalUuids) {
          removePendingLocalUuid(uuid);
        }
        checkAndApplyBufferedPatchesForAllContexts();

        // Track error for user feedback
        this._saveError = error instanceof Error ? error : new Error(String(error));
        throw error;
      }
    } else {
      // MODIFY - Instance already exists, must use makeEditable()
      throw new Error(
        `Cannot call save() on existing ${this.modelName} (id: ${this.id}). ` +
        `Use makeEditable() pattern instead:\n\n` +
        `  const editable = ${this.modelName.toLowerCase()}.makeEditable();\n` +
        `  editable.name = "New Name";\n` +
        `  editable.steps.add(newStep);\n` +
        `  await editable.save();`
      );
    }
  }

  /**
   * Recursively collect local UUIDs from nested relationship items.
   * Assigns local UUIDs to items that don't have IDs.
   */
  private collectNestedLocalUuids(pendingLocalUuids: string[]): void {
    const relationshipProps = this.getRelationshipProperties();

    for (const propName of relationshipProps) {
      try {
        const manager = (this as any)[propName];
        if (!manager?.getPendingOps) continue;

        const pendingOps = manager.getPendingOps();
        const addItems = pendingOps.add || [];

        for (const item of addItems) {
          if (!item) continue;

          // Assign local UUID if item doesn't have an ID
          if (!item.id) {
            const localId = localUuid();
            item.id = localId;
            item._localUuid = localId;
            pendingLocalUuids.push(localId);
          } else if (item.id.startsWith('local-')) {
            pendingLocalUuids.push(item.id);
            item._localUuid = item.id;
          }

          // Recurse into nested items
          if (typeof item.collectNestedLocalUuids === 'function') {
            item.collectNestedLocalUuids(pendingLocalUuids);
          }
        }
      } catch (e) {
        // Skip properties that error on access
        continue;
      }
    }
  }

  /**
   * Get or create a cached RelationshipManager for a property.
   * This ensures pending ops persist across multiple accesses.
   *
   * Uses a global WeakMap for automatic garbage collection - when this instance
   * is GC'd, its cached managers are automatically removed.
   *
   * @param propertyName - Name of the relationship property
   * @param factory - Factory function to create the manager if not cached
   * @returns Cached RelationshipManager instance
   */
  protected getOrCreateRelationshipManager<T>(
    propertyName: string,
    factory: () => T
  ): T {
    // Get or create the manager map for this instance
    let managerMap = relationshipManagerCache.get(this);
    if (!managerMap) {
      managerMap = new Map();
      relationshipManagerCache.set(this, managerMap);
    }

    // Get or create the specific manager
    if (!managerMap.has(propertyName)) {
      managerMap.set(propertyName, factory());
    }
    return managerMap.get(propertyName)!;
  }

  /**
   * Clear all cached relationship managers for this instance.
   * Called automatically after save() to prevent stale state.
   * Can also be called manually to force cleanup.
   *
   * Note: With WeakMap, this is optional - managers are automatically GC'd
   * when the instance is GC'd. But we clear explicitly after save() to ensure
   * fresh state.
   */
  clearRelationshipManagerCache(): void {
    const managerMap = relationshipManagerCache.get(this);
    if (managerMap) {
      managerMap.clear();
    }
  }

  /**
   * Get all relationship property names for this instance.
   * Discovers properties that return RelationshipManager instances.
   *
   * @returns Array of relationship property names
   */
  public getRelationshipProperties(): string[] {
    const props: string[] = [];
    const proto = Object.getPrototypeOf(this);

    // Iterate through all property descriptors on the prototype
    for (const key of Object.getOwnPropertyNames(proto)) {
      const descriptor = Object.getOwnPropertyDescriptor(proto, key);
      // Check if it's a getter (relationship managers are implemented as getters)
      if (descriptor?.get) {
        try {
          const value = (this as any)[key];
          // Check if the value is a RelationshipManager
          // RelationshipManager has propertyName and nodes getters
          if (value && typeof value.propertyName === 'string' && 'nodes' in value) {
            props.push(key);
          }
        } catch (e) {
          // Skip properties that throw errors when accessed
          continue;
        }
      }
    }

    return props;
  }

  /**
   * Delete this instance from the server (protected base implementation).
   *
   * This method is protected and should only be called by generated model classes
   * that have delete API registered in the backend.
   *
   * @param options - Delete options
   * @param options.cascade - Whether to cascade delete related nodes
   * @param options.dryRun - If true, return what would be deleted without actually deleting
   * @returns DeleteResult with deleted IDs and count
   */
  protected async _deleteInstance(options: {
    cascade?: boolean;
    dryRun?: boolean;
  } = {}): Promise<{ deletedIds: string[]; count: number }> {
    if (!this.id) {
      throw new Error(`Cannot delete ${this.modelName}: instance has no ID`);
    }

    const client = (this.constructor as typeof NeoNodeBase).apiClient;

    assertWritesAllowed();
    const response = await client.delete('/api/delete', {
      model_name: this.modelName,
      id: this.id,
      cascade: options.cascade,
      dry_run: options.dryRun ?? false
    });

    // Optimistically remove from GraphStore if not a dry run
    if (!options.dryRun && this.id) {
      graphStore.deleteNode(this.id);
    }

    return response;
  }

  /**
   * Public delete API for removing this node from the database.
   *
   * @param options - Delete options
   * @param options.cascade - Whether to cascade delete related nodes
   * @param options.dryRun - If true, return what would be deleted without actually deleting
   * @returns DeleteResult with deleted IDs and count
   */
  async delete(options: {
    cascade?: boolean;
    dryRun?: boolean;
  } = {}): Promise<{ deletedIds: string[]; count: number }> {
    return this._deleteInstance(options);
  }

  /**
   * Create a scoped editing session with change tracking and ConstructionSpec filtering.
   *
   * The EditableWrapper:
   * - Captures GraphStore baseline snapshot (the "before" state)
   * - Tracks all scalar property changes via Proxy
   * - Tracks all relationship changes via EditableRelationshipManager
   * - Filters operations by ConstructionSpec
   * - Diffs against baseline when building PATCH (removes redundant operations)
   * - Has scoped lifetime: create → edit → commit/discard
   *
   * @returns EditableWrapper instance that acts like the original node but tracks changes
   *
   * @example
   * ```typescript
   * // Create editing session
   * const editable = thread.makeEditable();
   *
   * // Make changes
   * editable.name = "New Name";
   * editable.steps.add(newStep);
   *
   * // Commit (sends PATCH with only actual changes)
   * await editable.commit();
   *
   * // Or discard
   * editable.discard();
   * ```
   */
  makeEditable(graphStoreInstance?: GraphStore) {
    return new EditableWrapper(this, graphStoreInstance || graphStore);
  }
}
